import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_THEME, normalizeTheme } from '../utils/theme';
import { scopedKey, getActiveAccountId } from '../utils/accountSession';
import OffcanvasRight from '../components/OffcanvasRight';

const THEME_FIELDS = [
  { key: 'color_primary', label: 'Primary color', type: 'color' },
  { key: 'color_text', label: 'Text color', type: 'color' },
  { key: 'color_text_secondary', label: 'Secondary text color', type: 'color' },
  { key: 'color_bg_base', label: 'Base background', type: 'color' },
  { key: 'color_bg_container', label: 'Card background', type: 'color' },
];
const THEME_FIELD_LABELS = THEME_FIELDS.reduce((acc, entry) => {
  acc[entry.key] = entry.label;
  return acc;
}, {
  font_family: 'Font family',
  font_size: 'Base font size',
});

const Styles = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('order_genie');
  const [orderGenieSettings, setOrderGenieSettings] = useState(null);
  const [orderGenieTheme, setOrderGenieTheme] = useState(DEFAULT_THEME);
  const [orderGenieNote, setOrderGenieNote] = useState('');
  const [ahriConfig, setAhriConfig] = useState(null);
  const [ahriTheme, setAhriTheme] = useState(DEFAULT_THEME);
  const [ahriNote, setAhriNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const successTimerRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyScope, setHistoryScope] = useState('order_genie');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [restoringId, setRestoringId] = useState(null);
  const [historyWidthPct, setHistoryWidthPct] = useState(50);
  const [livePreview, setLivePreview] = useState(true);
  const [editorOpen, setEditorOpen] = useState(true);
  const [editorWidthPct, setEditorWidthPct] = useState(40);
  const [resizing, setResizing] = useState(false);
  const resizeRef = useRef({ startX: 0, startWidth: 40 });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = getAuthHeaders();
      const [orderResp, ahriResp] = await Promise.all([
        fetch(`${API_BASE_URL}/api/v1/orderx/settings`, { headers }),
        fetch(`${API_BASE_URL}/api/v1/ahri/config`, { headers }),
      ]);
      const orderJson = await orderResp.json().catch(() => ({}));
      const ahriJson = await ahriResp.json().catch(() => ({}));
      if (!orderResp.ok) {
        throw new Error(orderJson?.detail || `OrderX settings failed (HTTP ${orderResp.status})`);
      }
      if (!ahriResp.ok) {
        throw new Error(ahriJson?.detail || `AHRI config failed (HTTP ${ahriResp.status})`);
      }
      setOrderGenieSettings(orderJson);
      setOrderGenieTheme(normalizeTheme(orderJson?.theme));
      setAhriConfig(ahriJson?.config || {});
      setAhriTheme(normalizeTheme(ahriJson?.config?.system_shop_theme));
    } catch (err) {
      setError(err.message || 'Unable to load theme settings.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const appendNote = (scope, message) => {
    if (!message) return;
    if (scope === 'order_genie') {
      setOrderGenieNote((prev) => (prev ? `${prev}\n${message}` : message));
      return;
    }
    setAhriNote((prev) => (prev ? `${prev}\n${message}` : message));
  };

  const applyPreview = (scope, themeValue) => {
    if (!livePreview) return;
    try {
      localStorage.setItem(scopedKey(`theme_preview_${scope}`, getActiveAccountId()), JSON.stringify(themeValue));
      window.dispatchEvent(new CustomEvent('theme-preview', { detail: { scope, theme: themeValue } }));
    } catch (err) {
      // ignore preview failures
    }
  };

  const clearPreview = (scope) => {
    try {
      localStorage.removeItem(scopedKey(`theme_preview_${scope}`, getActiveAccountId()));
      window.dispatchEvent(new CustomEvent('theme-preview', { detail: { scope, theme: null } }));
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    if (livePreview) return;
    clearPreview('order_genie');
    clearPreview('ahri_system_shop');
  }, [livePreview]);

  useEffect(() => {
    if (!success) return undefined;
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }
    successTimerRef.current = setTimeout(() => {
      setSuccess('');
    }, 3500);
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, [success]);

  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (event) => {
      const delta = resizeRef.current.startX - event.clientX;
      const deltaPct = (delta / window.innerWidth) * 100;
      const next = Math.min(60, Math.max(25, resizeRef.current.startWidth + deltaPct));
      setEditorWidthPct(next);
    };
    const onUp = () => setResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  const updateThemeValue = (scope, key, value) => {
    if (scope === 'order_genie') {
      setOrderGenieTheme((prev) => {
        const next = { ...prev, [key]: value };
        if (prev[key] !== value) {
          appendNote(scope, `${THEME_FIELD_LABELS[key] || key}: ${prev[key]} -> ${value}`);
        }
        applyPreview(scope, normalizeTheme(next));
        return next;
      });
      return;
    }
    setAhriTheme((prev) => {
      const next = { ...prev, [key]: value };
      if (prev[key] !== value) {
        appendNote(scope, `${THEME_FIELD_LABELS[key] || key}: ${prev[key]} -> ${value}`);
      }
      applyPreview(scope, normalizeTheme(next));
      return next;
    });
  };

  const updateFontValue = (scope, key, value) => {
    if (scope === 'order_genie') {
      setOrderGenieTheme((prev) => {
        const next = { ...prev, [key]: value };
        if (prev[key] !== value) {
          appendNote(scope, `${THEME_FIELD_LABELS[key] || key}: ${prev[key]} -> ${value}`);
        }
        applyPreview(scope, normalizeTheme(next));
        return next;
      });
      return;
    }
    setAhriTheme((prev) => {
      const next = { ...prev, [key]: value };
      if (prev[key] !== value) {
        appendNote(scope, `${THEME_FIELD_LABELS[key] || key}: ${prev[key]} -> ${value}`);
      }
      applyPreview(scope, normalizeTheme(next));
      return next;
    });
  };

  const loadHistory = useCallback(
    async (scope) => {
      setHistoryLoading(true);
      setHistoryError('');
      try {
        const headers = getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/api/v1/theme-history/${scope}`, { headers });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `History load failed (HTTP ${resp.status})`);
        }
        setHistoryItems(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        setHistoryError(err.message || 'Unable to load history.');
      } finally {
        setHistoryLoading(false);
      }
    },
    [getAuthHeaders]
  );

  const openHistory = useCallback(
    (scope) => {
      setHistoryScope(scope);
      setHistoryOpen(true);
      loadHistory(scope);
    },
    [loadHistory]
  );

  const handleSave = async (scope) => {
    const note = scope === 'order_genie' ? orderGenieNote.trim() : ahriNote.trim();
    if (!note) {
      setError('Add a short note before saving this theme version.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      if (scope === 'order_genie') {
        if (!orderGenieSettings) {
          throw new Error('OrderX settings are not loaded yet.');
        }
        const snapshot = normalizeTheme(orderGenieTheme);
        const payload = {
          ...orderGenieSettings,
          theme: snapshot,
        };
        const resp = await fetch(`${API_BASE_URL}/api/v1/orderx/settings`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `Save failed (HTTP ${resp.status})`);
        }
        setOrderGenieSettings(data);
        setOrderGenieTheme(normalizeTheme(data?.theme));
        setOrderGenieNote('');
        const historyResp = await fetch(`${API_BASE_URL}/api/v1/theme-history/order_genie`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ note, theme_snapshot: snapshot }),
        });
        if (!historyResp.ok) {
          const historyData = await historyResp.json().catch(() => ({}));
          throw new Error(historyData?.detail || `History save failed (HTTP ${historyResp.status})`);
        }
        clearPreview('order_genie');
        setSuccess('OrderX theme saved.');
      } else {
        const snapshot = normalizeTheme(ahriTheme);
        const payload = {
          ...(ahriConfig || {}),
          system_shop_theme: snapshot,
        };
        const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `Save failed (HTTP ${resp.status})`);
        }
        setAhriConfig(payload);
        setAhriTheme(normalizeTheme(payload.system_shop_theme));
        setAhriNote('');
        const historyResp = await fetch(`${API_BASE_URL}/api/v1/theme-history/ahri_system_shop`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ note, theme_snapshot: snapshot }),
        });
        if (!historyResp.ok) {
          const historyData = await historyResp.json().catch(() => ({}));
          throw new Error(historyData?.detail || `History save failed (HTTP ${historyResp.status})`);
        }
        clearPreview('ahri_system_shop');
        setSuccess('AHRI System Shop theme saved.');
      }
    } catch (err) {
      setError(err.message || 'Unable to save theme.');
    } finally {
      setSaving(false);
    }
  };

  const resetTheme = (scope) => {
    appendNote(scope, 'Reset to defaults');
    if (scope === 'order_genie') {
      setOrderGenieTheme(DEFAULT_THEME);
      applyPreview(scope, normalizeTheme(DEFAULT_THEME));
      return;
    }
    setAhriTheme(DEFAULT_THEME);
    applyPreview(scope, normalizeTheme(DEFAULT_THEME));
  };

  const renderThemeEditor = useCallback(
    (scope, themeState, noteValue, setNoteValue) => (
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="row g-3">
            {THEME_FIELDS.map((field) => (
              <div className="col-12 col-md-6 col-lg-4" key={`${scope}-${field.key}`}>
                <label className="form-label">{field.label}</label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={themeState[field.key] || DEFAULT_THEME[field.key]}
                    onChange={(e) => updateThemeValue(scope, field.key, e.target.value)}
                    title={field.label}
                  />
                  <input
                    type="text"
                    className="form-control"
                    value={themeState[field.key] || ''}
                    onChange={(e) => updateThemeValue(scope, field.key, e.target.value)}
                    placeholder={DEFAULT_THEME[field.key]}
                  />
                </div>
              </div>
            ))}
            <div className="col-12 col-md-6">
              <label className="form-label">Font family</label>
              <input
                type="text"
                className="form-control"
                value={themeState.font_family || ''}
                onChange={(e) => updateFontValue(scope, 'font_family', e.target.value)}
                placeholder={DEFAULT_THEME.font_family}
              />
              <div className="form-text">Comma-separated font stack.</div>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">Base font size</label>
              <input
                type="number"
                className="form-control"
                min={10}
                max={22}
                value={themeState.font_size ?? DEFAULT_THEME.font_size}
                onChange={(e) => updateFontValue(scope, 'font_size', e.target.value)}
              />
              <div className="form-text">Applies to the main body text size.</div>
            </div>
            <div className="col-12">
              <label className="form-label">Save note</label>
              <textarea
                className="form-control"
                rows={3}
                value={noteValue}
                onChange={(e) => setNoteValue(e.target.value)}
                placeholder="Describe why this theme version was saved"
              />
              <div className="form-text">Required to save and track version history.</div>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => resetTheme(scope)}
            >
              Reset defaults
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => clearPreview(scope)}
            >
              Clear preview
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => openHistory(scope)}
            >
              Version history
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving}
              onClick={() => handleSave(scope)}
            >
              {saving ? 'Saving...' : 'Save theme'}
            </button>
          </div>
        </div>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openHistory, saving]
  );

  const handleRestore = async (scope, historyId) => {
    setRestoringId(historyId);
    setError('');
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const resp = await fetch(`${API_BASE_URL}/api/v1/theme-history/${scope}/restore/${historyId}`, {
        method: 'POST',
        headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `Restore failed (HTTP ${resp.status})`);
      }
      await loadData();
      await loadHistory(scope);
      clearPreview(scope);
      setSuccess('Theme restored from history.');
    } catch (err) {
      setError(err.message || 'Unable to restore theme.');
    } finally {
      setRestoringId(null);
    }
  };

  const formatTimestamp = (value) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
    } catch (err) {
      return String(value);
    }
  };

  const tabConfig = useMemo(
    () => [
      { id: 'order_genie', label: 'OrderX', description: 'Theme settings for OrderX workflows.' },
      { id: 'ahri_system_shop', label: 'AHRI System Shop', description: 'Theme settings for the System Shop experience.' },
    ],
    []
  );

  return (
    <div className="position-relative" style={{ height: 'calc(100vh - 200px)' }}>
      <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
        <div>
          <h3 className="mb-1">Styles</h3>
          <div className="text-muted">Live preview with overlay editor.</div>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-primary" onClick={loadData} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => openHistory(activeTab)}
          >
            Version history
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setEditorOpen((prev) => !prev)}
          >
            {editorOpen ? 'Hide editor' : 'Show editor'}
          </button>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="styles_live_preview"
            checked={livePreview}
            onChange={(e) => setLivePreview(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="styles_live_preview">
            Live preview on module pages
          </label>
        </div>
        <div className="text-muted small">
          The module screen is rendered behind this panel.
        </div>
      </div>

      {(error || success) && (
        <div className="mb-3">
          {error && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
              <span>{error}</span>
              <button type="button" className="btn btn-sm btn-outline-light" onClick={() => setError('')}>
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}

      {success && (
        <div className="styles-toast alert alert-success" role="status">
          <span>{success}</span>
          <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setSuccess('')}>
            Dismiss
          </button>
        </div>
      )}

      <ul className="nav nav-tabs mb-4">
        {tabConfig.map((tab) => (
          <li className="nav-item" key={tab.id}>
            <button
              type="button"
              className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {tabConfig.map((tab) => (
        <div key={`${tab.id}-panel`} className={activeTab === tab.id ? '' : 'd-none'}>
          <div className="mb-3 text-muted">{tab.description}</div>
        </div>
      ))}

      <div className="position-relative" style={{ height: '100%', borderRadius: 16, overflow: 'hidden' }}>
        <iframe
          title="theme-preview"
          src={activeTab === 'order_genie' ? '/order-x' : '/ahri/shop'}
          style={{ width: '100%', height: '100%', border: 'none', background: '#f8fafc' }}
        />

        {editorOpen && (
          <div
            className="position-absolute top-0 end-0 h-100 bg-white border-start shadow"
            style={{ width: `${editorWidthPct}%`, maxWidth: '100%', overflowY: 'auto' }}
          >
            <div
              className="position-absolute top-0 start-0 h-100"
              style={{ width: 8, cursor: 'col-resize' }}
              onMouseDown={(event) => {
                setResizing(true);
                resizeRef.current = { startX: event.clientX, startWidth: editorWidthPct };
              }}
            />
            <div className="p-3">
              {activeTab === 'order_genie' &&
                renderThemeEditor('order_genie', orderGenieTheme, orderGenieNote, setOrderGenieNote)}
              {activeTab === 'ahri_system_shop' &&
                renderThemeEditor('ahri_system_shop', ahriTheme, ahriNote, setAhriNote)}
              <div className="mt-3">
                <label className="form-label small">Editor width</label>
                <input
                  type="range"
                  className="form-range"
                  min={25}
                  max={60}
                  value={editorWidthPct}
                  onChange={(e) => setEditorWidthPct(Number(e.target.value))}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <OffcanvasRight
        title="Theme version history"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        widthPct={historyWidthPct}
      >
        <div className="d-flex align-items-center gap-3 mb-3">
          <div className="flex-grow-1">
            <div className="fw-semibold text-capitalize">{historyScope.replace('_', ' ')}</div>
            <div className="text-muted small">Review and restore previous theme versions.</div>
          </div>
          <div style={{ minWidth: 160 }}>
            <label className="form-label small">Panel width</label>
            <input
              type="range"
              className="form-range"
              min={30}
              max={75}
              value={historyWidthPct}
              onChange={(e) => setHistoryWidthPct(Number(e.target.value))}
            />
          </div>
        </div>

        {historyError && (
          <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
            <span>{historyError}</span>
            <button type="button" className="btn btn-sm btn-outline-light" onClick={() => setHistoryError('')}>
              Dismiss
            </button>
          </div>
        )}

        {historyLoading && <div className="text-muted">Loading history...</div>}
        {!historyLoading && historyItems.length === 0 && (
          <div className="text-muted">No versions saved yet.</div>
        )}

        {!historyLoading &&
          historyItems.map((item) => {
            const snapshot = normalizeTheme(item?.theme_snapshot);
            return (
              <div key={`history-${item.id}`} className="card mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <div className="fw-semibold">{formatTimestamp(item?.created_at)}</div>
                      <div className="text-muted small">{item?.note || ''}</div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      disabled={restoringId === item.id}
                      onClick={() => handleRestore(historyScope, item.id)}
                    >
                      {restoringId === item.id ? 'Restoring...' : 'Restore'}
                    </button>
                  </div>
                  <div className="row g-2">
                    {THEME_FIELDS.map((field) => (
                      <div className="col-6" key={`${item.id}-${field.key}`}>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 6,
                              border: '1px solid #e5e7eb',
                              background: snapshot[field.key],
                            }}
                          />
                          <div className="small">{snapshot[field.key]}</div>
                        </div>
                        <div className="text-muted small">{field.label}</div>
                      </div>
                    ))}
                    <div className="col-12">
                      <div className="text-muted small">Typography</div>
                      <div className="small">{snapshot.font_family}</div>
                      <div className="small">Font size: {snapshot.font_size}px</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </OffcanvasRight>

      <style>{`
        .styles-toast {
          position: fixed;
          top: 92px;
          right: 24px;
          z-index: 1050;
          margin: 0;
          max-width: min(360px, 90vw);
          padding: 8px 12px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
        }
        .styles-toast .btn {
          padding: 2px 8px;
          font-size: 11px;
          line-height: 1.2;
        }
      `}</style>
    </div>
  );
};

export default Styles;
